/**
 * Custom hook for managing chat database initialization and state
 * Provides database initialization with retry logic and error handling
 */

import { useCallback, useEffect, useState } from 'react';
import { chatService } from '../services/chatService';
import type { UseChatDatabaseReturn, ChatError } from '../types/chat';

export function useChatDatabase() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<ChatError | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  /**
   * Initialize the database
   */
  const initializeDatabase = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      await chatService.initializeDatabase();
      
      setIsInitialized(true);
      setRetryCount(0);
      console.log('🗄️ Database initialized successfully');
    } catch (err) {
      const chatError = chatService.handleError(err);
      setError(chatError);
      setIsInitialized(false);
      console.error('Failed to initialize database:', chatError);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Retry database initialization
   */
  const retry = useCallback(async () => {
    setRetryCount(prev => prev + 1);
    console.log(`🔄 Retrying database initialization (attempt ${retryCount + 1})`);
    await initializeDatabase();
  }, [initializeDatabase, retryCount]);

  /**
   * Check if database is available
   */
  const checkDatabaseHealth = useCallback(async (): Promise<boolean> => {
    try {
      if (!isInitialized) return false;
      
      // Try to perform a simple operation to verify database health
      await chatService.getStatistics();
      return true;
    } catch (err) {
      console.error('Database health check failed:', err);
      setError(chatService.handleError(err));
      setIsInitialized(false);
      return false;
    }
  }, [isInitialized]);

  /**
   * Get database statistics
   */
  const getStats = useCallback(async () => {
    try {
      if (!isInitialized) {
        throw new Error('Database not initialized');
      }
      
      return await chatService.getStatistics();
    } catch (err) {
      const chatError = chatService.handleError(err);
      setError(chatError);
      console.error('Failed to get database statistics:', chatError);
      return null;
    }
  }, [isInitialized]);

  /**
   * Clear all data (for testing or reset)
   */
  const clearAllData = useCallback(async () => {
    try {
      if (!isInitialized) {
        throw new Error('Database not initialized');
      }
      
      await chatService.clearAllData();
      console.log('🧹 Cleared all database data');
    } catch (err) {
      const chatError = chatService.handleError(err);
      setError(chatError);
      console.error('Failed to clear database data:', chatError);
    }
  }, [isInitialized]);

  /**
   * Reset database connection
   */
  const resetConnection = useCallback(async () => {
    setIsInitialized(false);
    setError(null);
    await initializeDatabase();
  }, [initializeDatabase]);

  /**
   * Check if IndexedDB is supported
   */
  const isIndexedDBSupported = useCallback((): boolean => {
    return typeof window !== 'undefined' && 'indexedDB' in window;
  }, []);

  /**
   * Get database error message for display
   */
  const getErrorMessage = useCallback((): string | null => {
    if (!error) return null;
    
    switch (error.type) {
      case 'database':
        return `Database error: ${error.message}. Try refreshing the page.`;
      case 'unknown':
        if (!isIndexedDBSupported()) {
          return 'Your browser does not support local storage. Please use a modern browser.';
        }
        return `Initialization failed: ${error.message}. Try refreshing the page.`;
      default:
        return error.message;
    }
  }, [error, isIndexedDBSupported]);

  /**
   * Check if we should allow retry
   */
  const canRetry = useCallback((): boolean => {
    return retryCount < 3 && isIndexedDBSupported() && !isLoading;
  }, [retryCount, isIndexedDBSupported, isLoading]);

  // Initialize database on mount
  useEffect(() => {
    initializeDatabase();
  }, [initializeDatabase]);

  // Periodic health check (every 30 seconds) when initialized
  useEffect(() => {
    if (!isInitialized) return;

    const healthCheckInterval = setInterval(async () => {
      const isHealthy = await checkDatabaseHealth();
      if (!isHealthy) {
        console.warn('Database health check failed, attempting to reconnect...');
        await resetConnection();
      }
    }, 30000);

    return () => clearInterval(healthCheckInterval);
  }, [isInitialized, checkDatabaseHealth, resetConnection]);

  // Actions object for external use
  const actions = {
    retry,
    resetConnection,
    clearAllData,
    checkHealth: checkDatabaseHealth,
    getStats,
  };

  const result: UseChatDatabaseReturn & {
    actions: typeof actions;
    retryCount: number;
    canRetry: () => boolean;
    getErrorMessage: () => string | null;
    isIndexedDBSupported: () => boolean;
  } = {
    isInitialized,
    isLoading,
    error,
    retry,
    actions,
    retryCount,
    canRetry,
    getErrorMessage,
    isIndexedDBSupported,
  };

  return result;
}